import { type EnvironmentRef, IMPORT_MAX_BYTES, parseDotenv, type SectionSummary } from '@fleequid-secrets/shared';
import { useQuery } from '@tanstack/react-query';
import { type CSSProperties, type ReactNode, useMemo, useState } from 'react';
import { api, messageOf } from '@/api/client';
import { plural } from '@/lib/format';
import { useDebounced } from '@/lib/hooks';
import { useOverlay } from '@/lib/overlay';
import { Cancelled, guarded, useShell } from '@/lib/shell';
import { Icon } from './Icon';

// A .env into a section: pasted (a Bitwarden note, a file's text) or dropped. What it will do is shown before it does
// anything: new keys, values that differ, keys only the section has, lines that are not entries. Then one transaction.
export function ImportDialog(props: {
    environment: EnvironmentRef;
    section: SectionSummary;
    initial: string;
    onClose: () => void;
    onImported: () => void;
}): ReactNode {
    useOverlay();
    const shell = useShell();
    const [content, setContent] = useState(props.initial);
    const [overwrite, setOverwrite] = useState(true);
    const [prune, setPrune] = useState(false);
    const [busy, setBusy] = useState(false);
    const [over, setOver] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const settled = useDebounced(content, 300);
    const local = useMemo(() => parseDotenv(content), [content]);
    const tooBig = new Blob([content]).size > IMPORT_MAX_BYTES;
    const plan = useQuery({
        queryKey: ['import-plan', props.section.id, settled],
        queryFn: ({ signal }) => api.planImport(props.section.id, settled, signal),
        enabled: settled.trim() !== '' && !tooBig,
        staleTime: Infinity,
        gcTime: 0,
    });
    const current = plan.data?.plan && settled === content ? plan.data.plan : null;
    const changes = current ? current.added.length + (overwrite ? current.changed.length : 0) + (prune ? current.missing.length : 0) : 0;
    const lines = content.split('\n');

    const readFile = (file: File): void => {
        void file.text().then((text) => setContent(text));
    };
    const apply = (): void => {
        if (!current || changes === 0) {
            return;
        }
        setBusy(true);
        setError(null);
        guarded(shell, props.environment, `import ${plural(changes, 'change')} into ${props.section.name}`, (confirm) =>
            api.applyImport(props.section.id, content, { overwrite, prune }, confirm),
        )
            .then(({ outcome }) => {
                const parts = [
                    outcome.added && `${outcome.added} new`,
                    outcome.changed && `${outcome.changed} changed`,
                    outcome.removed && `${outcome.removed} removed`,
                ].filter(Boolean);
                shell.say(`Imported into ${props.section.name}: ${parts.join(', ') || 'nothing to change'}`);
                props.onImported();
            })
            .catch((reason: unknown) => !(reason instanceof Cancelled) && setError(messageOf(reason)))
            .finally(() => setBusy(false));
    };

    return (
        <div className="scrim" onMouseDown={(event) => event.target === event.currentTarget && props.onClose()}>
            <div className="dialog wide float" onKeyDown={(event) => event.key === 'Escape' && props.onClose()}>
                <div className="sheet-head">
                    <div>
                        <h2>Import a .env</h2>
                        <p>
                            Into{' '}
                            <span className="envtag">
                                <i className="dot" style={{ '--c': props.environment.color } as CSSProperties} />
                                {props.environment.name} · {props.section.name}
                            </span>
                            . Read as the apps read it: quotes, comments, multiline values.
                        </p>
                    </div>
                    <button className="closebtn" onClick={props.onClose} title="Close (esc)">
                        <Icon name="x" size={17} />
                    </button>
                </div>
                <div className="import-body">
                    <textarea
                        className={`field ${over ? 'invalid' : ''}`}
                        value={content}
                        autoFocus
                        spellCheck={false}
                        placeholder={
                            'Paste the note or the file here, or drop a .env on it\n\nDATABASE_URL=postgres://…\nSTRIPE_SECRET_KEY=sk_live_…'
                        }
                        onChange={(event) => setContent(event.target.value)}
                        onDragOver={(event) => {
                            event.preventDefault();
                            setOver(true);
                        }}
                        onDragLeave={() => setOver(false)}
                        onDrop={(event) => {
                            event.preventDefault();
                            setOver(false);
                            const file = event.dataTransfer.files[0];
                            if (file) {
                                readFile(file);
                            }
                        }}
                    />
                    <div className="toolbar">
                        <label className="act small">
                            <Icon name="file" size={14} /> Choose a file
                            <input
                                type="file"
                                accept=".env,text/plain,*"
                                hidden
                                onChange={(event) => {
                                    const file = event.target.files?.[0];
                                    if (file) {
                                        readFile(file);
                                    }
                                }}
                            />
                        </label>
                        <span className="micro">
                            {tooBig ? 'Larger than 512 KB' : content.trim() ? `${plural(local.entries.length, 'variable')} read` : 'Nothing yet'}
                        </span>
                    </div>
                    {current && (
                        <>
                            <div className="plan-summary">
                                {current.added.length > 0 && <span className="chip good">{current.added.length} new</span>}
                                {current.changed.length > 0 && <span className="chip warn">{current.changed.length} differ</span>}
                                {current.unchanged.length > 0 && <span className="chip">{current.unchanged.length} the same</span>}
                                {current.missing.length > 0 && <span className="chip data">{current.missing.length} only in the section</span>}
                                {current.errors.length > 0 && <span className="chip bad">{plural(current.errors.length, 'line')} skipped</span>}
                                {current.duplicates.length > 0 && (
                                    <span className="chip warn">{current.duplicates.length} written twice: the last wins</span>
                                )}
                            </div>
                            {(current.added.length > 0 || current.changed.length > 0 || current.errors.length > 0 || current.warnings.length > 0) && (
                                <div className="plan-list">
                                    {current.errors.map((issue) => (
                                        <div key={`e${issue.line}`}>
                                            <span className="problem">
                                                Line {issue.line}: {issue.message}
                                                {lines[issue.line - 1] ? ` · ${(lines[issue.line - 1] as string).slice(0, 40)}` : ''}
                                            </span>
                                            <span className="chip bad">skipped</span>
                                        </div>
                                    ))}
                                    {current.warnings.map((issue) => (
                                        <div key={`w${issue.line}`}>
                                            <span className="problem warn">
                                                Line {issue.line}: {issue.message}
                                            </span>
                                            <span className="chip warn">check</span>
                                        </div>
                                    ))}
                                    {current.added.map((key) => (
                                        <div key={`a${key}`}>
                                            <span>{key}</span>
                                            <span className="chip good">new</span>
                                        </div>
                                    ))}
                                    {current.changed.map((key) => (
                                        <div key={`c${key}`}>
                                            <span>{key}</span>
                                            <span className={`chip ${overwrite ? 'warn' : ''}`}>{overwrite ? 'replaced' : 'kept'}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div className="plan-options">
                                {current.changed.length > 0 && (
                                    <button className={`toggle ${overwrite ? 'on' : ''}`} onClick={() => setOverwrite(!overwrite)}>
                                        <span className="sw" />
                                        Replace the {plural(current.changed.length, 'value')} that differ (the old ones stay in their history)
                                    </button>
                                )}
                                {current.missing.length > 0 && (
                                    <button className={`toggle ${prune ? 'on' : ''}`} onClick={() => setPrune(!prune)}>
                                        <span className="sw" />
                                        Delete the {plural(current.missing.length, 'variable')} the file does not have
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                    {error && <div className="form-error">{error}</div>}
                    <div className="dialog-actions">
                        <button className="act" onClick={props.onClose}>
                            Cancel
                        </button>
                        <button className="act primary" disabled={!current || changes === 0 || busy || plan.isFetching} onClick={apply}>
                            <Icon name="upload" size={15} />
                            {busy ? 'Importing…' : current && changes === 0 ? 'Nothing to change' : `Import ${plural(changes, 'change')}`}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
